import { useMemo, useState } from 'react'
import { useMutation } from '@apollo/client/react'
import Button from '../ui/Button'
import Input from '../ui/Input'
import FormError from '../ui/FormError'
import { UPDATE_PASSWORD_MUTATION } from '../../graphql/auth'
import { useLang } from '../../contexts/LangContext'

interface UpdatePasswordData {
  updatePassword: {
    user: {
      id: string
    } | null
    errors: string[]
  }
}

interface UpdatePasswordVariables {
  currentPassword: string
  password: string
  passwordConfirmation: string
}

interface FormState {
  currentPassword: string
  password: string
  passwordConfirmation: string
}

function passwordStrength(password: string, locale: 'el' | 'en') {
  if (!password) return { label: locale === 'el' ? 'Δεν έχει οριστεί' : 'Not set', tone: 'text-slate-500', width: 'w-0' }

  let score = 0
  if (password.length >= 8) score += 1
  if (/[A-Z]/.test(password)) score += 1
  if (/[a-z]/.test(password)) score += 1
  if (/\d/.test(password)) score += 1
  if (/[^A-Za-z0-9]/.test(password)) score += 1

  if (score <= 2) return { label: locale === 'el' ? 'Αδύναμος' : 'Weak', tone: 'text-rose-600', width: 'w-1/3' }
  if (score === 3 || score === 4) return { label: locale === 'el' ? 'Μέτριος' : 'Medium', tone: 'text-amber-600', width: 'w-2/3' }
  return { label: locale === 'el' ? 'Ισχυρός' : 'Strong', tone: 'text-emerald-600', width: 'w-full' }
}

export default function PasswordChangeForm() {
  const { locale, t } = useLang()
  const [form, setForm] = useState<FormState>({
    currentPassword: '',
    password: '',
    passwordConfirmation: '',
  })
  const [errors, setErrors] = useState<string[]>([])
  const [saved, setSaved] = useState(false)
  const [updatePassword, { loading }] = useMutation<UpdatePasswordData, UpdatePasswordVariables>(UPDATE_PASSWORD_MUTATION)
  const strength = useMemo(() => passwordStrength(form.password, locale), [form.password, locale])

  async function handleSave() {
    const nextErrors: string[] = []
    if (!form.currentPassword) nextErrors.push(t('Ο τρέχων κωδικός είναι υποχρεωτικός.', 'Current password is required.'))
    if (!form.password) nextErrors.push(t('Ο νέος κωδικός είναι υποχρεωτικός.', 'New password is required.'))
    if (form.password.length > 0 && form.password.length < 8) {
      nextErrors.push(t('Ο νέος κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες.', 'New password must be at least 8 characters.'))
    }
    if (form.password !== form.passwordConfirmation) {
      nextErrors.push(t('Οι νέοι κωδικοί δεν ταιριάζουν.', 'New password confirmation does not match.'))
    }

    if (nextErrors.length > 0) {
      setErrors(nextErrors)
      setSaved(false)
      return
    }

    try {
      const { data } = await updatePassword({
        variables: {
          currentPassword: form.currentPassword,
          password: form.password,
          passwordConfirmation: form.passwordConfirmation,
        },
      })

      const mutationErrors = data?.updatePassword.errors ?? [t('Δεν ήταν δυνατή η αλλαγή κωδικού.', 'Could not update your password.')]
      if (mutationErrors.length > 0) {
        setErrors(mutationErrors)
        setSaved(false)
        return
      }

      setErrors([])
      setSaved(true)
      setForm({ currentPassword: '', password: '', passwordConfirmation: '' })
    } catch {
      setErrors([t('Δεν ήταν δυνατή η αλλαγή κωδικού.', 'Could not update your password.')])
      setSaved(false)
    }
  }

  return (
    <section className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-950">{t('Αλλαγή κωδικού', 'Change password')}</h2>
          <p className="mt-2 text-sm leading-7 text-slate-600">
            {t('Ο κωδικός αποθηκεύεται ξεχωριστά από τα υπόλοιπα στοιχεία λογαριασμού.', 'Password changes save separately from your profile details.')}
          </p>
        </div>
        <Button type="button" onClick={handleSave} loading={loading}>
          {t('Αποθήκευση', 'Save')}
        </Button>
      </div>

      <div className="mt-6 space-y-4">
        <FormError errors={errors} />
        {saved && (
          <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
            {t('Ο κωδικός ενημερώθηκε.', 'Password updated.')}
          </div>
        )}

        <Input
          type="password"
          label={t('Τρέχων κωδικός', 'Current password')}
          value={form.currentPassword}
          onChange={(event) => {
            setSaved(false)
            setForm((current) => ({ ...current, currentPassword: event.target.value }))
          }}
          autoComplete="current-password"
          required
        />
        <Input
          type="password"
          label={t('Νέος κωδικός', 'New password')}
          value={form.password}
          onChange={(event) => {
            setSaved(false)
            setForm((current) => ({ ...current, password: event.target.value }))
          }}
          autoComplete="new-password"
          required
        />
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="font-medium text-slate-700">{t('Ισχύς κωδικού', 'Password strength')}</span>
            <span className={['font-semibold', strength.tone].join(' ')}>{strength.label}</span>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
            <div className={['h-full rounded-full bg-current transition-all', strength.tone, strength.width].join(' ')} />
          </div>
        </div>
        <Input
          type="password"
          label={t('Επιβεβαίωση νέου κωδικού', 'Confirm new password')}
          value={form.passwordConfirmation}
          onChange={(event) => {
            setSaved(false)
            setForm((current) => ({ ...current, passwordConfirmation: event.target.value }))
          }}
          autoComplete="new-password"
          required
        />
      </div>
    </section>
  )
}
